import React, { useEffect, useState } from "react";
import { Plus, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate, formatErr } from "../../lib/api";
import { Loading, EmptyState, PageHeader, StatusPill } from "../../components/Shared";
import { isNative } from "../../native";

const NATIVE = isNative();

const FLOW = ["requested", "acknowledged", "arranging", "scheduled", "in_progress", "completed"];
const FLOW_LABELS = { requested: "Requested", acknowledged: "Acknowledged", arranging: "Being arranged", scheduled: "Scheduled", in_progress: "In progress", completed: "Completed" };

export default function Requests() {
  const [requests, setRequests] = useState(null);
  const [types, setTypes] = useState([]);
  const [parents, setParents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: "", details: "", preferred_time: "", parent_id: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/family/requests").then((r) => setRequests(r.data));
  useEffect(() => {
    load().catch(() => setRequests([]));
    api.get("/family/request-types").then((r) => setTypes(r.data)).catch(() => {});
    api.get("/family/parents").then((r) => setParents(r.data)).catch(() => {});
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/family/requests", form);
      const waStatus = r.data?.whatsapp?.family_confirmation;
      if (["sent", "simulated_sent"].includes(waStatus)) toast.success("Request received successfully — WhatsApp confirmation sent");
      else if (waStatus === "skipped_no_phone") toast.success("Request received successfully — add your mobile in Communication settings to get WhatsApp confirmations");
      else toast.success("Request received successfully — WhatsApp notification pending");
      setShowForm(false); setForm({ type: "", details: "", preferred_time: "", parent_id: "", urgency: "normal" }); load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };
  const cancel = async (id) => {
    try { await api.post(`/family/requests/${id}/cancel`); toast.success("Request cancelled"); load(); }
    catch (err) { toast.error(formatErr(err)); }
  };
  if (!requests) return <Loading />;
  return (
    <div data-testid="requests-page">
      <PageHeader title="Service Requests" sub="Ask for anything — a visit, a nurse, hospital help or errands. We coordinate and report back."
        actions={<button onClick={() => setShowForm(!showForm)} className="p1-btn-primary !px-5 !py-2.5" data-testid="new-request-btn"><Plus className="h-4 w-4" /> Request Help</button>} />
      {showForm && (
        <form onSubmit={submit} className="p1-card mb-6 space-y-4 p-5" data-testid="request-form">
          <div><label className="p1-label">What do you need? *</label>
            <div className="grid gap-2 sm:grid-cols-3">
              {types.map((t) => (
                <button type="button" key={t} onClick={() => setForm({ ...form, type: t })}
                  className={`rounded-xl border-2 p-3 text-left text-xs font-semibold transition-colors ${form.type === t ? "border-emerald-800 bg-emerald-50 text-emerald-900" : "border-stone-200 text-stone-600 hover:border-stone-300"}`}
                  data-testid={`req-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t}</button>
              ))}
            </div>
          </div>
          {parents.length > 1 && (
            <div><label className="p1-label">For whom?</label>
              <select className="p1-input sm:!w-72" value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} data-testid="req-parent">
                <option value="">— Select parent —</option>
                {parents.map((p) => <option key={p.id} value={p.id}>{p.label ? `${p.label} — ` : ""}{p.name}</option>)}
              </select></div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className="p1-label">Details</label><input className="p1-input" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} placeholder="Anything we should know?" data-testid="req-details" /></div>
            <div><label className="p1-label">Preferred time</label><input className="p1-input" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} placeholder="e.g. Tomorrow morning" data-testid="req-time" /></div>
          </div>
          <div>
            <label className="p1-label">How urgent is this?</label>
            <div className="flex gap-2">
              {[["normal", "Normal"], ["urgent", "Urgent"]].map(([v, l]) => (
                <button type="button" key={v} onClick={() => setForm({ ...form, urgency: v })} data-testid={`req-urgency-${v}`}
                  className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${(form.urgency || "normal") === v ? (v === "urgent" ? "bg-red-600 text-white" : "bg-emerald-900 text-white") : "border border-stone-200 text-stone-500"}`}>{l}</button>
              ))}
            </div>
          </div>
          <button disabled={!form.type || busy} className="p1-btn-primary !px-5 !py-2.5 disabled:opacity-40" data-testid="req-submit">{busy ? "Submitting…" : "Submit Request"}</button>
        </form>
      )}
      {requests.length === 0 ? <EmptyState title="No requests yet" body="Tap 'Request Help' whenever your parents need anything." /> : (
        <div className="space-y-4">
          {requests.map((r) => (
            <div key={r.id} className="p1-card p-5" data-testid={`request-card-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="break-words font-heading font-semibold text-stone-900">{r.type}{r.parent_name ? <span className="ml-2 text-xs font-medium text-stone-400">· for {r.parent_name}</span> : null}</p>
                  <p className="break-words text-xs text-stone-500">Raised {fmtDate(r.created_at)}{r.details ? ` · ${r.details}` : ""}</p>
                  {(r.cost_estimate || r.provider_name) && (
                    <p className="mt-1 text-xs text-stone-600">{r.provider_name && <>Provider: <strong>{r.provider_name}</strong> · </>}{r.cost_estimate}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill status={r.status} />
                  {!["completed", "cancelled"].includes(r.status) && (
                    <button onClick={() => cancel(r.id)} className="rounded-full p-1.5 text-stone-400 hover:text-red-600" aria-label="Cancel request" data-testid={`req-cancel-${r.id}`}><XCircle className="h-5 w-5" /></button>
                  )}
                </div>
              </div>
              {NATIVE ? (
                <div className="mt-3" data-testid={`req-flow-${r.id}`}>
                  {r.status === "cancelled" ? (
                    <span className="inline-block rounded-full bg-stone-200 px-2.5 py-1 text-[10px] font-bold uppercase text-stone-500">Cancelled</span>
                  ) : (
                    <>
                      <div className="flex items-center gap-1" aria-hidden="true">
                        {FLOW.map((s, i) => (
                          <span key={s} className={`h-1.5 flex-1 rounded-full ${i <= FLOW.indexOf(r.status) ? "bg-emerald-800" : "bg-stone-200"}`} />
                        ))}
                      </div>
                      <p className="mt-2 text-xs font-semibold text-stone-600" data-testid={`req-step-${r.id}`}>
                        {FLOW_LABELS[r.status] || r.status.replace(/_/g, " ")} · step {FLOW.indexOf(r.status) + 1} of {FLOW.length}
                      </p>
                    </>
                  )}
                </div>
              ) : (
              <div className="mt-4 flex items-center gap-1" data-testid={`req-flow-${r.id}`}>
                {FLOW.map((s, i) => {
                  const idx = FLOW.indexOf(r.status);
                  const active = r.status === "cancelled" ? false : i <= idx;
                  return (
                    <React.Fragment key={s}>
                      <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${active ? "bg-emerald-900 text-white" : "bg-stone-100 text-stone-400"}`}>{FLOW_LABELS[s] || s.replace(/_/g, " ")}</span>
                      {i < FLOW.length - 1 && <span className={`h-0.5 flex-1 ${i < idx ? "bg-emerald-800" : "bg-stone-200"}`} />}
                    </React.Fragment>
                  );
                })}
                {r.status === "cancelled" && <span className="rounded-full bg-stone-200 px-2.5 py-1 text-[10px] font-bold uppercase text-stone-500">Cancelled</span>}
              </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
